import React, { useCallback, useState } from 'react'
import { postRequest } from 'src/lib/core/api-client'
import { successToast, errorToast } from 'src/lib/utils/toasters'
import { useUser } from 'src/lib/contexts/UserContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { apiURI } from 'src/lib/utils/constants'
import { Send } from 'react-feather'
import { X } from 'react-feather'
import useGTM from 'src/lib/hooks/useGTM'

const CustomerRewardsExpiryDownload: React.FC = () => {
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()
  const { user } = useUser()
  const { setLoaderProperties } = useLoader()
  const { sendGTMTrigger } = useGTM()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [expiryLimit, setExpiryLimit] = useState<number | ''>(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const downloadCustomerData = useCallback(async () => {
    try {
      if (typeof expiryLimit !== 'number' || expiryLimit <= 0) {
        errorToast('Please enter a valid expiry limit.')
        return
      }
      setIsSubmitting(true)
      const payload = {
        name: 'Customer Rewards Expiry',
        frequencyType: 'ONCE',
        columns: ['Total Balance'],
        type: 'rewards_expiry',
        emails: [user?.email],
        reportDateRange: {
          start: '2025-04-13 18:30',
          end: '2025-04-14 18:30'
        },
        expiryLimit: expiryLimit
      }
      setLoaderProperties({ isLoaderVisible: true, message: 'Requesting customer data download.' })
      await postRequest(`/dashboard/report/scheduler-create`, apiURI.CHECKOUT_NODE, payload)
      sendGTMTrigger('rewards_customer_expiry_data_downloaded', 'click')
      setExpiryLimit(0)
      successToast(`Export successful. Your report has been sent to ${user?.email}`)
      setIsDialogOpen(false)
    } catch (e) {
      console.error(e)
      errorToast('Error while downloading file. Please try again.')
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
      setIsSubmitting(false)
    }
  }, [user, merchant, expiryLimit])

  return (
    <>
      <div
        onClick={user?.isDemoUser ? undefined : () => setIsDialogOpen(true)}
        className={`flex items-center justify-between ${
          user?.isDemoUser ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <p>Export Customer reward expiry</p>
        <Send size='16' className='font-medium text-[#6E6E6E] stroke-[2.5px]' />
      </div>

      {isDialogOpen && (
        <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50'>
          <div className='bg-white rounded-lg shadow-lg  p-6' style={{ width: '30rem' }}>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-lg font-semibold'>Enter Expiry Limit</h2>
              <button className='text-gray-500 hover:text-gray-700' onClick={() => setIsDialogOpen(false)}>
                {' '}
                <X size={24} />
              </button>
            </div>

            <p className='mb-2 text-sm text-gray-600'>Please enter the number of days:</p>
            <input
              type='number'
              value={expiryLimit}
              onChange={e => {
                const value = e.target.valueAsNumber
                setExpiryLimit(isNaN(value) ? '' : value)
              }}
              className='border border-gray-300 p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-green-500  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' // Add appearance-none here
              placeholder='Number of days'
              onKeyDown={e => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                }
              }}
            />

            <div className='flex justify-end mt-4 space-x-4'>
              <button onClick={() => setIsDialogOpen(false)} className='text-gray-600 hover:text-gray-800'>
                Cancel
              </button>
              <button
                onClick={downloadCustomerData}
                disabled={expiryLimit === '' || expiryLimit <= 0 || isSubmitting}
                className={`px-4 py-2 rounded text-white ${
                  isSubmitting ? 'bg-gray-400' : 'bg-teal-500 hover:bg-teal-600'
                }`}
              >
                Get Report
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
export default CustomerRewardsExpiryDownload
